import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Send, Trash2, MapPin, BellRing } from "lucide-react";
import { pushSupported, getSubscriptionState, enablePush, disablePush } from "@/lib/push";

const FIELDS = [
    { key: "group_min_size", label: "Minimum group size", type: "number" },
    { key: "group_target_size", label: "Target group size", type: "number" },
    { key: "group_max_size", label: "Maximum group size", type: "number" },
    { key: "late_cancel_hours", label: "Late cancellation window (hours before outing)", type: "number" },
    { key: "chat_close_hours", label: "Group chat closes (hours after outing)", type: "number" },
    { key: "membership_price", label: "Membership price (USD / month)", type: "number" },
];

export default function Settings() {
    const qc = useQueryClient();
    const [purging, setPurging] = useState(false);
    const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
    const [form, setForm] = useState({});
    useEffect(() => { if (data) setForm(data); }, [data]);

    const { data: pushStats, refetch: refetchPush } = useQuery({ queryKey: ["admin-push-stats"], queryFn: async () => (await api.get("/admin/push-stats")).data });
    const { data: marketAlerts } = useQuery({ queryKey: ["admin-market-alerts"], queryFn: async () => (await api.get("/admin/market-alerts")).data, refetchInterval: 60000 });

    const [pushState, setPushState] = useState("loading");
    const [pushBusy, setPushBusy] = useState(false);
    useEffect(() => { if (pushSupported()) getSubscriptionState().then(setPushState); else setPushState("unsupported"); }, []);
    const toggleAdminPush = async () => {
        setPushBusy(true);
        try {
            if (pushState === "subscribed") { await disablePush(); setPushState("unsubscribed"); toast.success("Admin alerts turned off on this device."); }
            else { await enablePush(); setPushState("subscribed"); toast.success("Admin alerts on — you'll get a push when a new market opens."); }
        } catch (e) { toast.error(e.message || "Could not update notifications."); }
        finally { setPushBusy(false); }
    };
    const [ann, setAnn] = useState({ title: "", body: "", audience: "all" });
    const [sending, setSending] = useState(false);
    const sendAnnouncement = async () => {
        if (!ann.title.trim() || !ann.body.trim()) { toast.error("Add a title and a message."); return; }
        setSending(true);
        try {
            const res = (await api.post("/admin/announcements/send", ann)).data;
            toast.success(`Sent to ${res.sent} device${res.sent === 1 ? "" : "s"}${res.failed ? ` (${res.failed} failed)` : ""}.`);
            setAnn({ title: "", body: "", audience: ann.audience });
            refetchPush();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setSending(false); }
    };

    const save = async () => {
        try {
            const payload = {};
            FIELDS.forEach((f) => { payload[f.key] = f.type === "number" ? Number(form[f.key]) : form[f.key]; });
            payload.show_launch_buzz = !!form.show_launch_buzz;
            await api.put("/admin/settings", payload);
            toast.success("Settings saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div>
            <AdminHeader title="System Settings" subtitle="Configure group sizes, cancellation windows, and membership." />
            <div className="max-w-xl space-y-4 rounded-lg border border-zinc-200 bg-white p-6">
                {FIELDS.map((f) => (
                    <div key={f.key}>
                        <label className="text-sm font-600">{f.label}</label>
                        <Input data-testid={`setting-${f.key}`} type={f.type} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                    </div>
                ))}

                <div className="flex items-start justify-between gap-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                    <div>
                        <p className="text-sm font-600">Show launch buzz on the landing ticker</p>
                        <p className="mt-0.5 text-xs text-zinc-500">Blends seeded nationwide signups into the "Now nationwide" ticker so it looks lively at launch. Turn off once real signups pick up to show only genuine members.</p>
                    </div>
                    <Switch data-testid="setting-show-launch-buzz" checked={!!form.show_launch_buzz} onCheckedChange={(v) => setForm({ ...form, show_launch_buzz: v })} />
                </div>

                <Button onClick={save} data-testid="settings-save">Save settings</Button>
            </div>

            <div className="mt-8 max-w-xl space-y-3 rounded-lg border border-red-200 bg-red-50 p-6" data-testid="danger-zone">
                <div>
                    <p className="text-sm font-700 text-red-700">Danger zone — remove test &amp; demo data</p>
                    <p className="mt-0.5 text-xs text-red-600/80">Permanently deletes all test/demo accounts (test-domain emails) and their outings, groups, chats, notifications, and demo venues. Real member signups are never touched. Use this to clean up before launch.</p>
                </div>
                <Button variant="destructive" disabled={purging} data-testid="purge-demo-btn"
                    onClick={async () => {
                        if (!window.confirm("Remove ALL test/demo accounts and their data? Real signups are kept. This cannot be undone.")) return;
                        setPurging(true);
                        try {
                            const res = (await api.post("/admin/purge-demo-data")).data;
                            toast.success(`Cleaned up. (${res.summary})`);
                            qc.invalidateQueries();
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        finally { setPurging(false); }
                    }}>
                    <Trash2 className="mr-1 h-4 w-4" /> {purging ? "Removing…" : "Remove test & demo data"}
                </Button>
            </div>

            <div className="mt-8 max-w-xl space-y-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="admin-alerts-panel">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-2">
                        <BellRing className="mt-0.5 h-5 w-5 flex-none text-zinc-700" />
                        <div>
                            <p className="text-sm font-600">New-market alerts on this device</p>
                            <p className="mt-0.5 text-xs text-zinc-500">Get a push notification whenever a signup opens a brand-new city market. Install the app and keep this on to be notified instantly.</p>
                        </div>
                    </div>
                    {pushState === "unsupported" ? (
                        <span className="text-xs text-zinc-400">Not supported here</span>
                    ) : pushState === "denied" ? (
                        <span className="text-xs text-red-500">Blocked in browser</span>
                    ) : (
                        <Switch data-testid="admin-push-toggle" disabled={pushBusy || pushState === "loading"} checked={pushState === "subscribed"} onCheckedChange={toggleAdminPush} />
                    )}
                </div>
                <div>
                    <p className="text-xs font-600 text-zinc-500">Recent new markets</p>
                    {(marketAlerts || []).filter((a) => a.meta?.type === "new_market").length === 0 ? (
                        <p className="mt-2 text-sm text-zinc-400" data-testid="market-alerts-empty">No new markets yet. You'll see cities here as members sign up.</p>
                    ) : (
                        <ul className="mt-2 space-y-2" data-testid="market-alerts-list">
                            {(marketAlerts || []).filter((a) => a.meta?.type === "new_market").slice(0, 10).map((a) => (
                                <li key={a.id} className="flex items-center gap-3 rounded-lg border border-zinc-100 bg-zinc-50 p-3">
                                    <MapPin className="h-4 w-4 flex-none text-[#c15a3f]" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-600 text-zinc-800">{a.meta?.city}{a.meta?.state ? `, ${a.meta.state}` : ""}</p>
                                        <p className="truncate text-xs text-zinc-500">{a.body}</p>
                                    </div>
                                    <span className="flex-none text-xs text-zinc-400">{new Date(a.created_at).toLocaleDateString()}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="mt-8 max-w-xl space-y-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="announcement-composer">
                <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-zinc-700" />
                    <div>
                        <p className="text-sm font-600">Send a push announcement</p>
                        <p className="text-xs text-zinc-500">
                            Delivers to members who installed the app and enabled notifications.
                            {pushStats ? ` ${pushStats.total_subscriptions} device(s) subscribed, ${pushStats.active_subscriptions} active member device(s).` : ""}
                        </p>
                    </div>
                </div>
                <div>
                    <label className="text-sm font-600">Title</label>
                    <Input data-testid="announcement-title" value={ann.title} maxLength={80} onChange={(e) => setAnn({ ...ann, title: e.target.value })} placeholder="A new outing just opened near you" className="mt-1" />
                </div>
                <div>
                    <label className="text-sm font-600">Message</label>
                    <Textarea data-testid="announcement-body" value={ann.body} maxLength={240} onChange={(e) => setAnn({ ...ann, body: e.target.value })} placeholder="Tap to see what's happening this weekend." className="mt-1" rows={3} />
                </div>
                <div>
                    <label className="text-sm font-600">Audience</label>
                    <Select value={ann.audience} onValueChange={(v) => setAnn({ ...ann, audience: v })}>
                        <SelectTrigger data-testid="announcement-audience" className="mt-1 max-w-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All subscribed members</SelectItem>
                            <SelectItem value="active">Verified &amp; active members only</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button onClick={sendAnnouncement} disabled={sending} data-testid="announcement-send"><Send className="mr-1 h-4 w-4" /> {sending ? "Sending…" : "Send announcement"}</Button>
            </div>
        </div>
    );
}
